<template>
  <div ref="root" class="preview">
    <div
      v-for="(productInput, id, i) in productInputBySku"
      :id="String(id)"
      :ref="(el) => (productElements[i] = el as HTMLElement)"
      :key="id"
      :data-index="i"
      class="preview-product"
    >
      <strong v-if="!productsBySku?.[id] || productsBySku?.[id]?.hasTemporarySku" class="warning">
        {{ translate('Item no', 'attributes', 'capitalize') }}
        {{ appTranslate('missing', undefined, 'lowercase') }}
      </strong>
      <span v-else class="text" :class="[productsBySku?.[id]?.propertyStatuses?.sku]">
        <MoreReadableSkuComponent :sku="id?.toString()" />
      </span>

      <LoaderComponent v-if="shouldLoad(id.toString()) && loading" static size="small" />

      <span v-if="!productsBySku?.[id] && !loading" class="new-product ml-2">{{ appTranslate('New Product') }}</span>

      <template v-if="intersectionObserver.elementIsVisible(i)">
        <DisplayValueComponent :primary-value="getMainName(productsBySku?.[id])">
          <template #default="{ primaryValue }">
            <span v-if="primaryValue" class="text"> ({{ primaryValue }}) </span>
          </template>
        </DisplayValueComponent>
        <div class="ml-2">
          <PropertyComponent
            v-for="(property, j) of previewProperties"
            :key="j"
            class="mr-2"
            :property="property"
            always-show-label
            show-changes
            :product="productsBySku?.[id] || newProductsBySku?.[id]"
            :product-input="productInput"
            :always-resettable="alwaysResettable"
            show-if-contains="changes"
            @reset="bulkStore.setModified(id.toString(), property, $event.containsChanges), bulkStore.pushHistoryEntry()"
          />
        </div>
      </template>
    </div>

    <!-- <template v-if="!Object.keys(edited).length"> {{ appTranslate('No changes have been made') }} </template> -->
    <div v-if="!Object.keys(productInputBySku).length" class="mt-4 text-warning">{{ appTranslate('No changes present compared to live') }}</div>
  </div>
</template>

<script lang="ts" setup>
import LoaderComponent from '@/components/simple/LoaderComponent.vue'
import MoreReadableSkuComponent from '@/components/simple/MoreReadableSkuComponent.vue'
import { useIntersectionObserver } from '@/composables/useIntersectionObserver'
import { useTranslation } from '@/composables/useTranslation'
import { ProductInput } from '@/models/Import'
import { SimplifiedProduct } from '@/models/Product'
import { EditableAdvancedProperty } from '@/models/Property'
import DataService from '@/services/DataService'
import { useProductsStore } from '@/store/productsArchiveStore'
import { useProductsBulkStore } from '@/store/productsBulkStore'
import { arrayToRecord } from '@/utils/tools'
import { computed, nextTick, onMounted, ref } from 'vue'
import PropertyComponent from '../PropertyComponent.vue'
import { getMainName } from '@/utils/productUtils.js'
import DisplayValueComponent from '@/components/DisplayValueComponent.vue'

// -----------
// -- Setup --
// -----------
const props = defineProps<{
  previewProperties?: EditableAdvancedProperty[]
  productInputBySku: { [sku: string]: ProductInput }
  newProductsBySku?: Record<string, SimplifiedProduct | undefined> // used to place a "new" badge
  alwaysResettable?: boolean
}>()

const { translate, appTranslate } = useTranslation()
const productsStore = useProductsStore()
const bulkStore = useProductsBulkStore()

const intersectionObserver = useIntersectionObserver()
intersectionObserver.initIntersectionObserver(true, undefined, (index, element) => {
  const id = element?.id

  if (id && shouldLoad(id) && !loading.value) {
    loadProducts(index)
  }
})
const fetchLimit = 10

// ---------------
// -- Reactives --
// ---------------
const products = ref<SimplifiedProduct[]>([])
const loading = ref<Set<string | number>>()

const productElements = ref<{ [id: string]: HTMLElement }>({})

const productsBySku = computed(() => {
  const allProducts = [...productsStore.currentProducts, ...products.value]
  return arrayToRecord<SimplifiedProduct>(allProducts, 'sku')
})

// ---------------
// -- Lifecycle --
// ---------------
onMounted(() => {
  loadProducts().then(async () => {
    await nextTick()
    const elements = Object.values(productElements.value)
    intersectionObserver.observeAll(elements)
  })
})

// -------------
// -- Methods --
// -------------
const loadProducts = async (requestedIndex = 0) => {
  const from = Math.floor(requestedIndex / fetchLimit) * fetchLimit

  loading.value = new Set(Object.keys(props.productInputBySku).slice(from, from + fetchLimit))

  const skus = Object.keys(props.productInputBySku).slice(from, from + fetchLimit)

  const response = await DataService.getProducts(
    {
      _field_sku: skus,
    },
    { includeAllCultures: true },
    { doNotUseStandardOptionsAsFallback: true },
  )

  let index = from
  for (const product of response.data) {
    index++
    products.value[index] = product
  }

  bulkStore.updateAllModified(skus, productsBySku.value, props.productInputBySku)
  loading.value = undefined
}

const shouldLoad = (id: string) => !productsBySku.value[id] && !props.newProductsBySku?.[id]
</script>

<style lang="scss" scoped>
.preview {
  position: relative;
  min-height: 50px;
}
.new-product {
  $colour: #51d225;
  font-weight: bold;
  color: $colour;
  border: 1px solid $colour;
  border-radius: 5px;
  padding: 2px 4px;
}

.warning {
  color: var(--warning);
}

.preview-product {
  border: 1px solid #ddd;
  padding: 1em;
  margin-bottom: 1em;
  background-color: #fff;
}
</style>
